import debounce from 'lodash/debounce'
import type { MaybeRef, Ref, VNodeRef } from 'vue'
import { computed, inject, onMounted, onUnmounted, onUpdated, ref, unref, watch } from 'vue'

import type { UpdateData } from '#src/js/WebsocketUpdateModule.mts'
import type { ProvidedUpdateData, UpdateDataWrapper } from '#src/vue/updateSource.mts'
import { INJECTION_SYMBOL } from '#src/vue/updateSource.mts'

export type UpdateCallback = (el: HTMLElement) => void
const onUpdateCallbacks: Record<string, UpdateCallback> = {}

export type ElementRef = Ref<HTMLElement | undefined | null> | VNodeRef

let pendingRefs: ElementRef[] = []

/**
 * When we don't have any expected reliance on actual updateData
 * (like in BootstrapRemoteContent), we want to simply runCallbacks
 *
 * once when we've fetched data.
 * @param ref
 * @returns
 */
export function runCallbacks (ref: ElementRef) {
  pendingRefs.push(ref)
  runCallbacksDebounce()
}

const runCallbacksDebounce = debounce(
  () => {
    // console.debug("runCallbacks", pendingRefs.length)
    const _list = pendingRefs.splice(0, pendingRefs.length)
    const elements: Element[] = []
    _list.forEach((ref) => {
      // ignore null refs
      const _new = unref(ref)
      if (!_new) {
        return
      }
      // ignore refs if an ancestor exists
      if (elements.some((el) => el.contains(_new))) {
        return
      }
      // find any children of incoming element and remove them.
      const children = elements.filter((el) => _new.contains(el))
      children.forEach(el => {
        const index = elements.indexOf(el)
        if (index !== -1) {
          delete elements[index]
        }
      })

      elements.push(_new)
    })
    elements.forEach(el => {
      if (!el) {
        return
      }
      Object.values(onUpdateCallbacks)
        .forEach(callback => {
          try {
            callback(el as HTMLElement)
          } catch (error) {
            console.error(error)
          }
        })
      })
  },
  50,
  {
    leading: true,
    trailing: true,
  },
)

export function registerUpdateCallback (key: string, callback: UpdateCallback) {
  onUpdateCallbacks[key] = callback
}

export function injectUpdateData<Sections extends any>() {
  type ResultData = ProvidedUpdateData<UpdateDataWrapper<Sections>>
  return inject<ResultData | undefined>(INJECTION_SYMBOL, undefined) ?? { pathRef: undefined, updateDataRef: undefined }
}

function _injectUpdateDataRef<Data extends UpdateData<any> | undefined>() {
  const { updateDataRef } = inject<ProvidedUpdateData<Data> | undefined>(INJECTION_SYMBOL, undefined) ?? {}
  return updateDataRef
}

export function useUpdateConsumer<Sections extends any> (elementRef?: ElementRef, updateData?: Ref<UpdateData<Sections> | undefined>) {

  type Data = UpdateData<Sections> | undefined

  const _updateDataRef = updateData === undefined
    ? _injectUpdateDataRef<Data>()
    : updateData

  if (_updateDataRef === undefined) {
    throw new Error("useUpdateConsumer could not find any update data.")
  }

  // dummy data so we don't crash on the above error
  const updateDataRef = _updateDataRef ?? ref(undefined)

  const loaded = computed(() => {
    const { meta } = unref(updateDataRef) ?? {}
    return meta?.cachedData || meta?.initialized
  })
  const loading = computed(() => {
    return !loaded.value
  })
  const updateId = computed(() => (unref(updateDataRef)?.updateId))
  const sections = computed(() => (unref(updateDataRef)?.sections))

  if (elementRef !== undefined) {
    useUpdateWatcher(elementRef, updateDataRef)
  }

  return {
    loaded,
    loading,
    updateId,
    sections,
    updateData: updateDataRef,
  } as const
}

export function useUpdateWatcher (ref: ElementRef, updateDataRef: MaybeRef<UpdateData<any> | undefined>) {
  if (updateDataRef !== undefined) {
    const unwatch = watch(
      () => (unref(updateDataRef)?.updateId),
      () => runCallbacks(ref),
      { flush: 'post' },
    )

    onUnmounted(() => unwatch())
  }

  // run callbacks when the Component is mounted.
  onMounted(() => {
    runCallbacks(ref)
  })

  // run callbacks when the Component is updated
  onUpdated(() => {
    runCallbacks(ref)
  })

  // run callbacks when the ref element changes, thus allowing for references to non-root items.
  watch(
    [ref],
    () => runCallbacks(ref),
    { flush: 'post' },
  )
}

export {
  UpdateData
}
